import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SysDiscipline } from 'app/modules/referrer/book-online/models/sys-discipline.model';
import { BookOnlineClinic } from 'app/shared/clinics/models/book-online-clinic.model';
import { ClinicGroupFranchise } from 'app/shared/clinics/models/clinic-group-franchise.model';
import { ClinicOfficeHour } from 'app/shared/clinics/models/clinic-office-hour.model';
import { Clinic } from 'app/shared/clinics/models/clinic.model';
import { ClinicSearchResultByLocation } from 'app/shared/clinics/models/clinicSearchResultByLocation.model';
import { BookingAppSessionCreation } from 'app/shared/portal/models/booking-app-session-creation.model';
import { BookingAppSession } from 'app/shared/portal/models/booking-app-session.model';
import { ClientStatus } from 'app/shared/portal/models/client-status.model';
import { ClinicOnlineBanner } from 'app/shared/portal/models/clinic-online-banner.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { ClinicClientPortalCardSetup } from '../models/clinic-client-portal-card-setup.model';
import { ClinicReferrerPortalSetup } from '../models/clinic-referrer-portal-setup.model';
import { ClinicApplicationSetup } from 'app/modules/referrer/book-online/models/clinic-application-setup.model';

@Injectable({
  providedIn: 'root',
})
export class PortalSetupHttpService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getClinicApplicationSetup(): Observable<ClinicApplicationSetup> {
    const url = `${this.baseUrl}clinicapplicationsetups`;
    return this.http.get<ClinicApplicationSetup>(url);
  }

  getClinicOnlineReferrerSetup(): Observable<ClinicReferrerPortalSetup> {
    const url = `${this.baseUrl}referrerportalsetups`;
    return this.http.get<ClinicReferrerPortalSetup>(url);
  }

  getClinicClientPortalCards(): Observable<ClinicClientPortalCardSetup[]> {
    const url = `${this.baseUrl}clinics/clientportalcards`;
    return this.http.get<ClinicClientPortalCardSetup[]>(url);
  }

  getClinic(clinicId: number): Observable<Clinic> {
    const url = `${this.baseUrl}clinics/${clinicId}`;
    return this.http.get<Clinic>(url);
  }

  getClientPortalStatus(): Observable<ClientStatus> {
    const url = `${this.baseUrl}clients/clientportalstatus`;
    return this.http.get<ClientStatus>(url);
  }

  getReferrerPortalStatus(): Observable<number> {
    const url = `${this.baseUrl}referrerportaltermandconditionacceptationaudit/latestaudit`;
    return this.http.get<number>(url);
  }

  getFranchise(subDomain: string): Observable<ClinicGroupFranchise> {
    const url = `${this.baseUrl}clinicgroups/referralportal/?subDomain=${subDomain}`;
    return this.http.get<ClinicGroupFranchise>(url);
  }

  getClinicOnlineReferrerBanners(sysBannerTypeId: number): Observable<ClinicOnlineBanner[]> {
    const url = `${this.baseUrl}referrerportal/cliniconlinebannersetups?sysBannerTypeId=${sysBannerTypeId}`;
    return this.http.get<ClinicOnlineBanner[]>(url).pipe(map(banners => (banners ? banners : [])));
  }

  getEffectiveClinicOfficeHour(): Observable<ClinicOfficeHour> {
    const url = `${this.baseUrl}clinicofficehours/effective`;
    return this.http.get<ClinicOfficeHour>(url);
  }

  getClientPortalClinics(): Observable<BookOnlineClinic[]> {
    const url = `${this.baseUrl}bookonline/clinics`;
    return this.http.get<BookOnlineClinic[]>(url);
  }

  getMyClinics(): Observable<number[]> {
    const url = `${this.baseUrl}clinics/myclinics`;
    return this.http.get<number[]>(url);
  }

  createBookingAppSession(session: BookingAppSessionCreation): Observable<BookingAppSession> {
    const url = `${this.baseUrl}bookingappsessions`;
    return this.http.post<BookingAppSession>(url, session);
  }

  getClinicLocation(
    latitude: number,
    longitude: number,
    radiusInKm: number
  ): Observable<ClinicSearchResultByLocation[]> {
    const url = `${this.baseUrl}clinics/search/location/?latitude=${latitude}&longitude=${longitude}&radiusInKm=${radiusInKm}`;
    return this.http.get<ClinicSearchResultByLocation[]>(url);
  }

  getSysDisciplines(): Observable<SysDiscipline[]> {
    const url = `${this.baseUrl}sysdisciplines/online`;
    return this.http.get<SysDiscipline[]>(url);
  }

  getModulesByFranchises(): Observable<number[]> {
    const url = `${this.baseUrl}clinicgroupfranchises/modules/sysapplicationmodules`;
    return this.http.get<number[]>(url);
  }

  getModulesByClinic(): Observable<number[]> {
    const url = `${this.baseUrl}clinics/modules/sysapplicationmodules`;
    return this.http.get<number[]>(url);
  }

  getReferrerPortalSetups(): Observable<any> {
    const url = `${this.baseUrl}clinics/referrerportalsetups`;
    return this.http.get<any>(url);
  }
}
